import { useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useApiCall } from 'redux/utils/apiHook.hook';
import {
  HospitalizationDto,
  HospitalizationForm,
  HospitalizationFormSchema,
  UserDto,
} from '@act/shared/data-transfer-objects';
import { hospitalizationsRequests } from 'redux/requests/hospitalizations.requests';
import { GenericDialogProps } from 'utilities/dialog-context/DialogContextHelpers';
import { Dialog, DialogContent } from '@mui/material';
import {
  DialogContentContainer,
  DialogTitleBarComponent,
} from './components/SharedDialogHookComponents';
import { DatadogReplayPhi, DatadogReplayStatic, FlexContainer, FormBuilder } from '@act/storybook';
import { FormConfig } from 'stories/forms/FormBuilder/formBuilder.types';
import SubmittableDialogActions from './components/dialog-components/SubmittableDialogActions';
import { useToast } from '../app/ReusableToast';
import useUnsavedChangesDialog from './hooks/useUnsavedChangesDialog.hook';
import { useAppDispatch } from 'redux/storeHooks';
import { TrackingProvider } from 'react-components/tracking';
import { updateHospitalizationSuccess } from 'redux/slices/hospitalizations.slice';
import { awaitFlushPendingNoteAutosave } from './flushPendingNoteAutosave.util';
import { handleNoteFieldClose } from './noteFieldClose.util';
import { useHospitalizationNoteSaveOverride } from './hooks/useHospitalizationNoteSaveOverride.hook';
import { NoteFieldRef } from './NoteField';

export interface HospitalizationDialogInterfaceProps {
  user: UserDto;
  hospitalization: HospitalizationDto;
  callback?: () => void;
}

export type HospitalizationDialogProps = GenericDialogProps<void> &
  HospitalizationDialogInterfaceProps;

export type HospitalizationDialogCallbackInput = void;

const HospitalizationDialog = ({
  hospitalization,
  user,
  open,
  setOpen,
  callback,
}: HospitalizationDialogProps) => {
  const toast = useToast();
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const dispatch = useAppDispatch();
  const noteFieldRef = useRef<NoteFieldRef>(null);
  const [
    updatedHospitalization,
    isUpdatingHospitalization,
    updateHospitalization,
  ] = useApiCall(hospitalizationsRequests.updateHospitalization);

  const { hospitalizationNoteSaveOverride } = useHospitalizationNoteSaveOverride({
    hospitalizationId: hospitalization.id,
  });

  const { control, formState, handleSubmit } = useForm<HospitalizationForm>({
    resolver: zodResolver(HospitalizationFormSchema),
    defaultValues: {
      note: hospitalization?.note ?? '<p><br></p>',
    },
  });

  const formConfig: FormConfig<HospitalizationForm> = useMemo(
    () => [
      {
        type: 'input',
        name: 'note',
        label: 'Note',
        input: 'noteField',
        props: {
          ref: noteFieldRef,
          mode: 'edit',
          itemType: 'hospitalization',
          itemId: String(hospitalization.id),
          noteId: hospitalization.noteId,
          userId: user.id,
          showCharacterCount: true,
          trackingName: 'hospitalization-note-input',
          save: hospitalizationNoteSaveOverride,
        },
      },
    ],
    [
      hospitalization.id,
      hospitalization.noteId,
      hospitalizationNoteSaveOverride,
      user.id,
    ]
  );

  const handleSave = async (data: HospitalizationForm) => {
    if (isUpdatingHospitalization) {
      return;
    }

    await awaitFlushPendingNoteAutosave(noteFieldRef);

    updateHospitalization({
      params: { id: hospitalization.id },
      body: { note: data.note },
    });
  };

  const { isDirty } = formState;
  const handleClose = () => {
    handleNoteFieldClose({
      mode: 'edit',
      isDirty,
      noteFieldRef,
      handleUnsavedChanges,
      finishClose: () => setOpen(false),
    });
  };

  useEffect(() => {
    if (updatedHospitalization) {
      dispatch(updateHospitalizationSuccess(updatedHospitalization));
      toast('Hospitalization updated successfully');
      callback?.();
      setOpen(false);
    }
  }, [updatedHospitalization, dispatch, toast, callback, setOpen]);

  return (
    <TrackingProvider contextName="hospitalization-dialog">
      <FlexContainer direction="column" width="600px">
        <Dialog
          open={open}
          onClose={handleClose}
          onSubmit={handleSubmit(handleSave)}
          fullWidth
        >
          <DialogContentContainer>
            <DialogTitleBarComponent
              title={
                <>
                  <DatadogReplayStatic>
                    Editing Hospitalization for{' '}
                  </DatadogReplayStatic>
                  <DatadogReplayPhi>
                    {user?.firstName} {user?.lastName}
                  </DatadogReplayPhi>
                </>
              }
              handleClose={handleClose}
            />
            <DialogContent>
              <FormBuilder
                config={formConfig}
                control={control}
                formState={formState}
              />
            </DialogContent>
          </DialogContentContainer>
          <SubmittableDialogActions
            cancelText={'Cancel'}
            submissionText={'Save'}
            isValid={formState.isValid}
            handleClose={handleClose}
            handleSubmission={handleSubmit(handleSave)}
          />
        </Dialog>
      </FlexContainer>
    </TrackingProvider>
  );
};

export default HospitalizationDialog;
